import { useEffect, useRef, useState } from "react";
import { Link, useBlocker } from "react-router-dom";
import { toast } from "sonner";
import {
  DEFAULT_HERO_PLACEHOLDER,
  brandByKey,
  type SiteCatalogLayout,
  type SiteConfig,
  type SiteEditorState,
  type SiteHeroStyle,
  type SitePhotoFit,
  type SiteTypography,
} from "@school-sales-ai/shared";
import { PageHeader } from "@/components/PageHeader";
import { SiteImageField } from "@/components/site/SiteImageField";
import { SiteHoursEditor } from "@/components/site/SiteHoursEditor";
import { SiteListEditor } from "@/components/site/SiteListEditor";
import {
  SiteSectionCard,
  SiteSegmentedField,
  SiteSelectField,
  SiteTextField,
  SiteToggleField,
  type SiteChoice,
} from "@/components/site/SiteFields";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useClientBranding } from "@/hooks/useClientBranding";
import { useScopedClientId } from "@/hooks/useScopedClientId";
import { useSettingsQuery } from "@/hooks/useSettings";
import {
  useCreateSitePreview,
  usePublishSite,
  useRestoreSitePublication,
  useSiteDraft,
  useSiteEditorState,
  useSitePublications,
  type SiteDraftEditor,
  type SiteSaveStatus,
} from "@/hooks/useSiteEditor";

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_LABEL: Record<SiteSaveStatus, string> = {
  saved: "Borrador guardado",
  pending: "Guardando…",
  saving: "Guardando…",
  invalid: "Sin guardar: corrige los campos marcados",
  error: "No se pudo guardar; se reintentará al editar",
};

/** Every key of the configuration that is a section (all but the version stamp). */
type SiteSection = Exclude<keyof SiteConfig, "schemaVersion">;

const HERO_STYLES: ReadonlyArray<SiteChoice<SiteHeroStyle>> = [
  { value: "dark", label: "Oscura" },
  { value: "light", label: "Clara" },
];
const CATALOG_LAYOUTS: ReadonlyArray<SiteChoice<SiteCatalogLayout>> = [
  { value: "sections", label: "Secciones" },
  { value: "mosaic", label: "Mosaico" },
  { value: "rows", label: "Filas" },
  { value: "list", label: "Lista" },
];
const TYPOGRAPHIES: ReadonlyArray<SiteChoice<SiteTypography>> = [
  { value: "industrial", label: "Industrial" },
  { value: "artisanal", label: "Artesanal" },
  { value: "modern", label: "Moderna" },
];
const PHOTO_FITS: ReadonlyArray<SiteChoice<SitePhotoFit>> = [
  { value: "contain", label: "Recortadas" },
  { value: "cover", label: "Ambiente" },
];

/** An empty string means "not set" for the schema's nullable text fields. */
function orNull(value: string): string | null {
  return value.trim() === "" ? null : value;
}

/**
 * Admin → «Sitio web» (plan feature `website`, docs/plans/ai-first-site.md).
 *
 * The draft saves itself as the admin types; the public site changes only on
 * «Publicar». What the site reads live from the rest of the CRM (logo, colour,
 * catalog, WhatsApp…) is listed apart and never part of the draft — that
 * asymmetry is Decision 4, and it must be visible here.
 */
export function SitioWeb(): React.JSX.Element {
  const clientId = useScopedClientId();
  const { data: state, isPending, isError } = useSiteEditorState();

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {isPending ? (
        <>
          <PageHeader title="Sitio web" />
          <p className="p-6 text-[13px] text-ink-500">Cargando…</p>
        </>
      ) : isError || !state ? (
        <>
          <PageHeader title="Sitio web" />
          <p className="p-6 text-[13px] text-red-500">No se pudo cargar el sitio.</p>
        </>
      ) : (
        // Keyed by client: a super-admin switching tenants gets a fresh editor,
        // never one tenant's unsaved draft under another's name.
        <SiteEditor key={clientId ?? "none"} state={state} />
      )}
    </div>
  );
}

function SiteEditor({ state }: { state: SiteEditorState }): React.JSX.Element {
  const editor = useSiteDraft(state.draft);
  const publish = usePublishSite();
  const preview = useCreateSitePreview();
  const busy = publish.isPending || preview.isPending;
  const canPublish = !busy && editor.status !== "invalid" && (state.hasUnpublishedChanges || editor.dirty);

  async function openPreview(): Promise<void> {
    // Opened inside the click so no popup blocker stops it; pointed at the link
    // once the pending edits are saved and the link is minted.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    const saved = await editor.flush();
    if (!saved) toast.warning("La vista previa no incluye los campos con errores.");
    try {
      const link = await preview.mutateAsync();
      if (tab) tab.location.href = link.url;
      else window.location.assign(link.url);
    } catch {
      tab?.close();
    }
  }

  async function handlePublish(): Promise<void> {
    if (!(await editor.flush())) {
      toast.error("Corrige los campos marcados antes de publicar.");
      return;
    }
    publish.mutate();
  }

  return (
    <>
      <UnsavedChangesGuard editor={editor} />
      <PageHeader
        title="Sitio web"
        subtitle={
          state.publishedAt
            ? `Publicado el ${formatDateTime(state.publishedAt)}${
                state.hasUnpublishedChanges || editor.dirty ? " · Hay cambios sin publicar" : ""
              }`
            : "Aún no publicado"
        }
        right={
          <>
            <span
              className={`hidden text-[12px] md:inline ${editor.status === "invalid" || editor.status === "error" ? "text-red-500" : "text-ink-500"}`}
              aria-live="polite"
            >
              {STATUS_LABEL[editor.status]}
            </span>
            <button
              type="button"
              onClick={() => void openPreview()}
              disabled={busy}
              className="rounded-lg border border-ink-300 px-3 py-1.5 text-[13px] font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-50"
            >
              Vista previa
            </button>
            <button
              type="button"
              onClick={() => void handlePublish()}
              disabled={!canPublish}
              className="rounded-lg bg-lime px-3 py-1.5 text-[13px] font-semibold text-moss hover:bg-lime-dk disabled:opacity-40"
            >
              {publish.isPending ? "Publicando…" : "Publicar"}
            </button>
          </>
        }
      />
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <p className="text-[12px] text-ink-500 md:hidden" aria-hidden>
            {STATUS_LABEL[editor.status]}
          </p>
          <SiteSections editor={editor} assetBaseUrl={state.assetBaseUrl} />
          <FromTheCrm />
          <Domains hostnames={state.hostnames} />
          <History
            beforeRestore={editor.flush}
            onRestored={(next) => editor.reseed(next.draft)}
            hasUnpublishedChanges={editor.dirty || state.hasUnpublishedChanges}
          />
        </div>
      </div>
    </>
  );
}

/**
 * Leaving the page saves what is pending first, as the catalog does. Only a
 * draft that cannot be saved (a field with an error) stops the navigation.
 */
function UnsavedChangesGuard({ editor }: { editor: SiteDraftEditor }): React.JSX.Element {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => editor.dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  const [stuck, setStuck] = useState(false);
  const flushing = useRef(false);
  const { flush } = editor;

  useEffect(() => {
    if (blocker.state !== "blocked" || flushing.current) return;
    flushing.current = true;
    void (async () => {
      const saved = await flush();
      flushing.current = false;
      if (saved) blocker.proceed();
      else setStuck(true);
    })();
  }, [blocker, flush]);

  return (
    <AlertDialog open={stuck} onOpenChange={(open) => !open && setStuck(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Hay cambios sin guardar</AlertDialogTitle>
          <AlertDialogDescription>
            Algún campo tiene un error y no se pudo guardar. Si sales ahora, esos cambios se pierden.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              setStuck(false);
              if (blocker.state === "blocked") blocker.reset();
            }}
          >
            Quedarme
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              setStuck(false);
              if (blocker.state === "blocked") blocker.proceed();
            }}
          >
            Salir sin ellos
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SiteSections({ editor, assetBaseUrl }: { editor: SiteDraftEditor; assetBaseUrl: string }): React.JSX.Element {
  const { draft, errors, update } = editor;
  const { data: settings } = useSettingsQuery();
  const { data: branding } = useClientBranding();
  const maxSizeMb = settings?.logoMaxSizeMb;
  const brand = brandByKey(branding?.brand);
  const err = (path: string): string | undefined => errors.get(path);

  /** Replaces one section, keeping the rest of the draft. */
  function set<K extends SiteSection>(key: K, patch: Partial<SiteConfig[K]>): void {
    update((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  }

  const { style, hero, trust, catalog, about, testimonials, location, footer, seo } = draft;
  return (
    <>
      <SiteSectionCard title="Estilo" description="El aspecto del sitio, a partir de estilos ya diseñados.">
        <SiteSegmentedField
          label="Portada"
          value={style.hero}
          options={HERO_STYLES}
          hint="Oscura: la foto con un velo oscuro y el texto en blanco. Clara: para fotos muy claras."
          onChange={(value) => set("style", { hero: value })}
        />
        <SiteSelectField
          label="Catálogo en portada"
          value={style.catalogLayout}
          options={CATALOG_LAYOUTS}
          hint="Secciones: de 6 a 10 categorías. Mosaico: catálogos muy visuales. Filas: muchas categorías. Lista: catálogos técnicos o con pocas fotos."
          onChange={(value) => set("style", { catalogLayout: value })}
        />
        <SiteSegmentedField
          label="Tipografía"
          value={style.typography}
          options={TYPOGRAPHIES}
          hint="Industrial: condensada y firme. Artesanal: con remates, cálida. Moderna: para servicios o tecnología."
          onChange={(value) => set("style", { typography: value })}
        />
        <SiteSegmentedField
          label="Fotos de producto"
          value={style.photoFit}
          options={PHOTO_FITS}
          hint="Recortadas: piezas sobre fondo blanco, se muestran completas. Ambiente: fotos del producto en uso, llenan la tarjeta."
          onChange={(value) => set("style", { photoFit: value })}
        />
      </SiteSectionCard>

      <SiteSectionCard
        title="Portada"
        description="Lo primero que ve el visitante, con el agente al centro. El saludo y las opciones iniciales del agente se editan en Agente › Ajustes."
      >
        <SiteTextField
          label="Titular"
          value={hero.headline}
          max={80}
          placeholder="Dinos qué pieza necesitas"
          hint="Vacío, el sitio muestra el nombre del negocio."
          error={err("hero.headline")}
          onChange={(headline) => set("hero", { headline })}
        />
        <SiteTextField
          label="Subtítulo"
          value={hero.subheadline}
          max={160}
          error={err("hero.subheadline")}
          onChange={(subheadline) => set("hero", { subheadline })}
        />
        <SiteTextField
          label="Texto del campo de búsqueda"
          value={hero.inputPlaceholder}
          max={60}
          placeholder={DEFAULT_HERO_PLACEHOLDER}
          hint="Lo que se lee dentro de la barra donde el visitante le escribe al agente."
          error={err("hero.inputPlaceholder")}
          onChange={(inputPlaceholder) => set("hero", { inputPlaceholder })}
        />
        <SiteImageField
          label="Imagen de fondo"
          hint="Opcional; va detrás del titular, con el velo que elijas en «Estilo»."
          path={hero.imagePath}
          assetBaseUrl={assetBaseUrl}
          maxSizeMb={maxSizeMb}
          onChange={(imagePath) => set("hero", { imagePath })}
        />
      </SiteSectionCard>

      <SiteSectionCard
        title="Confianza"
        description="Las marcas que distribuyes o con las que trabajas, y algunas cifras."
        enabled={trust.enabled}
        onEnabledChange={(enabled) => set("trust", { enabled })}
      >
        <SiteTextField
          label="Título"
          value={trust.title}
          max={60}
          placeholder="Distribuidores autorizados"
          error={err("trust.title")}
          onChange={(title) => set("trust", { title })}
        />
        <SiteListEditor
          items={trust.items}
          max={8}
          itemLabel={(i) => `Marca ${i + 1}`}
          addLabel="Agregar marca"
          newItem={() => ({ name: "", logoPath: null, url: null })}
          onChange={(items) => set("trust", { items })}
          renderItem={(item, i, setItem) => (
            <>
              <SiteTextField
                label="Nombre"
                value={item.name}
                max={60}
                error={err(`trust.items.${i}.name`)}
                onChange={(name) => setItem({ ...item, name })}
              />
              <SiteTextField
                label="Enlace (opcional)"
                type="url"
                value={item.url ?? ""}
                placeholder="https://"
                error={err(`trust.items.${i}.url`)}
                onChange={(url) => setItem({ ...item, url: orNull(url) })}
              />
              <SiteImageField
                label="Logo"
                path={item.logoPath}
                assetBaseUrl={assetBaseUrl}
                maxSizeMb={maxSizeMb}
                onChange={(logoPath) => setItem({ ...item, logoPath })}
              />
            </>
          )}
        />
        <SiteListEditor
          items={trust.figures}
          max={4}
          itemLabel={(i) => `Cifra ${i + 1}`}
          addLabel="Agregar cifra"
          newItem={() => ({ value: "", label: "" })}
          onChange={(figures) => set("trust", { figures })}
          renderItem={(figure, i, setFigure) => (
            <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
              <SiteTextField
                label="Valor"
                value={figure.value}
                max={20}
                placeholder="25 años"
                error={err(`trust.figures.${i}.value`)}
                onChange={(value) => setFigure({ ...figure, value })}
              />
              <SiteTextField
                label="Descripción"
                value={figure.label}
                max={40}
                placeholder="en el mercado"
                error={err(`trust.figures.${i}.label`)}
                onChange={(label) => setFigure({ ...figure, label })}
              />
            </div>
          )}
        />
      </SiteSectionCard>

      <SiteSectionCard
        title="Catálogo"
        description="Tus categorías y productos activos, tal como están en Catálogo, cada uno con su propia página."
        enabled={catalog.enabled}
        onEnabledChange={(enabled) => set("catalog", { enabled })}
      >
        <SiteTextField
          label="Título"
          value={catalog.title}
          max={60}
          placeholder="Nuestros productos"
          error={err("catalog.title")}
          onChange={(title) => set("catalog", { title })}
        />
      </SiteSectionCard>

      <SiteSectionCard
        title="Quiénes somos"
        enabled={about.enabled}
        onEnabledChange={(enabled) => set("about", { enabled })}
      >
        <SiteTextField
          label="Título"
          value={about.title}
          max={60}
          error={err("about.title")}
          onChange={(title) => set("about", { title })}
        />
        <SiteTextField
          label="Texto"
          multiline
          rows={8}
          value={about.body}
          max={1500}
          hint="Admite **negritas**, listas y enlaces en formato Markdown."
          error={err("about.body")}
          onChange={(body) => set("about", { body })}
        />
        <SiteImageField
          label="Imagen"
          hint="Opcional."
          path={about.imagePath}
          assetBaseUrl={assetBaseUrl}
          maxSizeMb={maxSizeMb}
          onChange={(imagePath) => set("about", { imagePath })}
        />
      </SiteSectionCard>

      <SiteSectionCard
        title="Testimonios"
        description="Solo testimonios reales, con permiso de quien los dio."
        enabled={testimonials.enabled}
        onEnabledChange={(enabled) => set("testimonials", { enabled })}
      >
        <SiteListEditor
          items={testimonials.items}
          max={6}
          itemLabel={(i) => `Testimonio ${i + 1}`}
          addLabel="Agregar testimonio"
          newItem={() => ({ quote: "", author: "", role: null, logoPath: null })}
          onChange={(items) => set("testimonials", { items })}
          renderItem={(item, i, setItem) => (
            <>
              <SiteTextField
                label="Testimonio"
                multiline
                rows={3}
                value={item.quote}
                max={300}
                error={err(`testimonials.items.${i}.quote`)}
                onChange={(quote) => setItem({ ...item, quote })}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <SiteTextField
                  label="Nombre"
                  value={item.author}
                  max={80}
                  error={err(`testimonials.items.${i}.author`)}
                  onChange={(author) => setItem({ ...item, author })}
                />
                <SiteTextField
                  label="Puesto o empresa (opcional)"
                  value={item.role ?? ""}
                  max={80}
                  error={err(`testimonials.items.${i}.role`)}
                  onChange={(role) => setItem({ ...item, role: orNull(role) })}
                />
              </div>
              <SiteImageField
                label="Logo de la empresa"
                path={item.logoPath}
                assetBaseUrl={assetBaseUrl}
                maxSizeMb={maxSizeMb}
                onChange={(logoPath) => setItem({ ...item, logoPath })}
              />
            </>
          )}
        />
      </SiteSectionCard>

      <SiteSectionCard
        title="Ubicación y contacto"
        description="El enlace al mapa y el WhatsApp se toman de Agente › Ajustes y del número conectado."
        enabled={location.enabled}
        onEnabledChange={(enabled) => set("location", { enabled })}
      >
        <SiteTextField
          label="Dirección"
          value={location.address}
          max={200}
          error={err("location.address")}
          onChange={(address) => set("location", { address })}
        />
        <SiteTextField
          label="Correo (opcional)"
          type="email"
          value={location.email ?? ""}
          error={err("location.email")}
          onChange={(email) => set("location", { email: orNull(email) })}
        />
        <SiteHoursEditor
          hours={location.hours}
          onChange={(hours) => set("location", { hours })}
          errorAt={(i) =>
            [...errors.entries()].find(([path]) => path === `location.hours.${i}` || path.startsWith(`location.hours.${i}.`))?.[1]
          }
        />
      </SiteSectionCard>

      <SiteSectionCard title="Pie de página">
        <SiteToggleField
          label={`Mostrar «Asistente por ${brand.name}»`}
          checked={footer.showCredit}
          onChange={(showCredit) => set("footer", { showCredit })}
        />
      </SiteSectionCard>

      <SiteSectionCard
        title="Buscadores y redes"
        description="Cómo aparece la página de inicio en Google y al compartirla. Vacíos, se usan el nombre del negocio y el subtítulo."
      >
        <SiteTextField
          label="Título"
          value={seo.title}
          max={70}
          error={err("seo.title")}
          onChange={(title) => set("seo", { title })}
        />
        <SiteTextField
          label="Descripción"
          multiline
          rows={2}
          value={seo.description}
          max={160}
          error={err("seo.description")}
          onChange={(description) => set("seo", { description })}
        />
      </SiteSectionCard>
    </>
  );
}

/** What the site reads live from the rest of the CRM — published the moment it is saved there. */
function FromTheCrm(): React.JSX.Element {
  const rows: Array<{ what: string; where: string; to: string }> = [
    { what: "Logo, color, saludo y opciones iniciales del agente, enlace al mapa", where: "Agente › Ajustes", to: "/agent" },
    { what: "Categorías, productos, descripciones y fotos", where: "Catálogo", to: "/catalogo" },
    { what: "Número de WhatsApp", where: "WhatsApp", to: "/whatsapp-connect" },
  ];
  return (
    <section className="rounded-lg border border-ink-100 bg-white p-4">
      <h2 className="text-[15px] font-semibold text-ink-900">Viene del CRM</h2>
      <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">
        Esto no es parte del borrador: el sitio lo lee en vivo, así que un cambio ahí se ve en el sitio (y en el
        agente) sin «Publicar».
      </p>
      <ul className="mt-3 flex flex-col gap-2 text-[13px]">
        {rows.map((row) => (
          <li key={row.to} className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-ink-700">{row.what}</span>
            <Link to={row.to} className="font-medium text-moss hover:underline">
              {row.where}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Domains({ hostnames }: { hostnames: string[] }): React.JSX.Element {
  return (
    <section className="rounded-lg border border-ink-100 bg-white p-4">
      <h2 className="text-[15px] font-semibold text-ink-900">Dominio</h2>
      {hostnames.length === 0 ? (
        <p className="mt-1 text-[13px] text-ink-500">
          Aún no tiene dominio. Lo conecta nuestro equipo de soporte; mientras tanto puedes armarlo y revisarlo con
          «Vista previa».
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1 text-[13px]">
          {hostnames.map((host) => (
            <li key={host}>
              <a href={`https://${host}`} target="_blank" rel="noopener noreferrer" className="font-medium text-moss hover:underline">
                {host}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function History({
  beforeRestore,
  onRestored,
  hasUnpublishedChanges,
}: {
  /** Settles any save in flight first, so it cannot land on top of the restored draft. */
  beforeRestore: () => Promise<unknown>;
  onRestored: (state: SiteEditorState) => void;
  hasUnpublishedChanges: boolean;
}): React.JSX.Element {
  const { data: publications } = useSitePublications();
  const restore = useRestoreSitePublication();
  const [target, setTarget] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    if (!target) return;
    const id = target;
    setTarget(null);
    await beforeRestore();
    try {
      onRestored(await restore.mutateAsync(id));
    } catch {
      // Reported by the mutation's own toast; the draft is left as it was.
    }
  }

  return (
    <section className="rounded-lg border border-ink-100 bg-white p-4">
      <h2 className="text-[15px] font-semibold text-ink-900">Publicaciones</h2>
      {!publications || publications.length === 0 ? (
        <p className="mt-1 text-[13px] text-ink-500">Todavía no se ha publicado ninguna versión.</p>
      ) : (
        <ul className="mt-2 divide-y divide-ink-100 text-[13px]">
          {publications.map((p, i) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2">
              <span className="text-ink-700">
                {formatDateTime(p.publishedAt)}
                {p.publishedByName ? ` · ${p.publishedByName}` : ""}
                {i === 0 && <span className="ml-2 text-[11px] font-semibold uppercase text-moss">En línea</span>}
              </span>
              {i > 0 && (
                <button
                  type="button"
                  onClick={() => setTarget(p.id)}
                  disabled={restore.isPending}
                  className="font-medium text-moss hover:underline disabled:opacity-50"
                >
                  Restaurar
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <AlertDialog open={target !== null} onOpenChange={(open) => !open && setTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Restaurar esta versión?</AlertDialogTitle>
            <AlertDialogDescription>
              Se publica de nuevo y el borrador vuelve a ser esa versión.
              {hasUnpublishedChanges ? " Los cambios que no has publicado se pierden." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirm()}>Restaurar y publicar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
